import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";

/* The main menu. Settled with James on 2026-09-25.
 *
 * Grouped by the job a person is doing, not by how the engine works. A group
 * with one page is a plain link, because a dropdown with one item is an extra
 * click for nothing. A group appears only when it has a real page in it.
 *
 * Where this goes as the roadmap is built, in docs/ux-decisions.md:
 * Campaigns becomes "Sales" when Programs and Assignments exist, and Today,
 * Incentives and Reports arrive with their first pages.
 *
 * Every page is listed here once. Add a page by adding a line.
 *
 * The dropdowns are disclosure buttons, not an ARIA menu, because they hold
 * links. They open on click, never on hover, because touch has no hover.
 *
 * Link, not NavLink. NavLink drops an aria-current it did not work out itself,
 * so a campaign's run page read as outside Campaigns. onPage decides it here. */

type Page = { label: string; to: string; also?: string[]; adminOnly?: true };
type Group = { label: string; pages: Page[] };

const MENU: (Page | Group)[] = [
  // The campaigns the signed-in person owns, where a person who owns one
  // lands (docs/plans/campaign-assignment.md, milestone 10). Everyone sees
  // it, and a person who owns none reads that on the page.
  { label: "My campaigns", to: "/my-campaigns" },
  // Every campaign, because it is what a campaign builder signs in to do.
  // James, 2026-09-22. A campaign's run page belongs to it.
  { label: "Campaigns", to: "/campaigns", also: ["/runs"] },
  { label: "Records", pages: [
    { label: "Companies", to: "/companies" },
    { label: "People", to: "/people" },
    { label: "Properties", to: "/properties" },
    { label: "Duplicates", to: "/duplicates" },
  ] },
  { label: "Add data", pages: [
    { label: "Import a file", to: "/import" },
    { label: "Submit a page", to: "/submit" },
    { label: "Enrichment", to: "/enrichment" },
  ] },
  // Everyone sees Jobs, Ingestion and Sources. Alerts and Processing are the
  // pages only an admin sees, because api.event (engine migration 154) and
  // the processing reads (migration 164) are granted to saleshive_admin alone.
  { label: "Admin", pages: [
    { label: "Jobs", to: "/jobs" },
    { label: "Ingestion", to: "/ingestion" },
    { label: "Sources", to: "/sources" },
    { label: "Alerts", to: "/alerts", adminOnly: true },
    { label: "Processing", to: "/processing", adminOnly: true },
    { label: "Styleguide", to: "/styleguide", adminOnly: true },
  ] },
];

/* The menu a person with this role sees: every page, less the admin-only
   ones unless they are an admin. */
function menuFor(role: string | null): (Page | Group)[] {
  const admin = role === "saleshive_admin";
  return MENU.map((entry) =>
    isGroup(entry) ? { ...entry, pages: entry.pages.filter((p) => admin || !p.adminOnly) } : entry,
  ).filter((entry) => !isGroup(entry) || entry.pages.length > 0);
}

const isGroup = (x: Page | Group): x is Group => "pages" in x;

function onPage(path: string, page: Page) {
  return [page.to, ...(page.also ?? [])].some((p) => path === p || path.startsWith(p + "/"));
}

export function MainMenu({ email, role, onSignOut }: {
  email: string; role: string | null; onSignOut: () => void;
}) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState<string | null>(null);
  const bar = useRef<HTMLDivElement>(null);

  // Moving to another page closes whatever was open.
  useEffect(() => { setOpen(null); }, [pathname]);

  // A click anywhere outside the bar closes it.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (bar.current && !bar.current.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const toggle = (label: string) => setOpen((o) => (o === label ? null : label));

  return (
    <div className="menubar" ref={bar}>
      <nav aria-label="Main">
        <ul className="menu">
          {menuFor(role).map((entry) =>
            isGroup(entry) ? (
              <Dropdown key={entry.label} label={entry.label}
                        current={entry.pages.some((p) => onPage(pathname, p))}
                        open={open === entry.label}
                        onToggle={() => toggle(entry.label)} onClose={() => setOpen(null)}>
                {entry.pages.map((p) => (
                  <li key={p.to}>
                    <Link to={p.to} className="menu-item"
                             aria-current={onPage(pathname, p) ? "page" : undefined}>
                      {p.label}
                    </Link>
                  </li>
                ))}
              </Dropdown>
            ) : (
              <li key={entry.to}>
                <Link to={entry.to} className="menu-link"
                         aria-current={onPage(pathname, entry) ? "page" : undefined}>
                  {entry.label}
                </Link>
              </li>
            ))}
        </ul>
      </nav>

      {/* Said on the bar, not inside the account menu, because nothing works
          until it is fixed. */}
      {role ? null : <span className="menu-warning">No group. Ask James to add you.</span>}

      <ul className="menu menu-account">
        <Dropdown label={email} open={open === "account"} align="right"
                  onToggle={() => toggle("account")} onClose={() => setOpen(null)}>
          <li className="menu-note">Signed in as {role ? role.replace("saleshive_", "") : "no group"}</li>
          <li>
            <button type="button" className="menu-item" onClick={onSignOut}>Sign out</button>
          </li>
        </Dropdown>
      </ul>
    </div>
  );
}

/* One dropdown. The button says whether it is open. The arrow keys move
   through the list, Escape closes it and returns to the button, and tabbing
   out of it closes it. */
function Dropdown({ label, current = false, open, align = "left", onToggle, onClose, children }: {
  label: string; current?: boolean; open: boolean; align?: "left" | "right";
  onToggle: () => void; onClose: () => void; children: ReactNode;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [focusFirst, setFocusFirst] = useState(false);

  const items = () => Array.from(list.current?.querySelectorAll<HTMLElement>(".menu-item") ?? []);

  // Opened from the keyboard, the first item takes the focus.
  useEffect(() => {
    if (open && focusFirst) { items()[0]?.focus(); setFocusFirst(false); }
  }, [open, focusFirst]);

  const onTriggerKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) onToggle();
      setFocusFirst(true);
    }
  };

  const onListKey = (e: KeyboardEvent) => {
    const all = items();
    const at = all.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => { e.preventDefault(); all[(i + all.length) % all.length]?.focus(); };
    if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(all.length - 1);
    else if (e.key === "Escape") { e.preventDefault(); onClose(); trigger.current?.focus(); }
  };

  return (
    <li className="menu-group"
        onKeyDown={(e) => { if (e.key === "Escape" && open) { e.stopPropagation(); onClose(); trigger.current?.focus(); } }}
        onBlur={(e) => { if (open && !e.currentTarget.contains(e.relatedTarget as Node)) onClose(); }}>
      <button ref={trigger} type="button" className={current ? "menu-trigger current" : "menu-trigger"}
              aria-expanded={open} aria-controls={id} onClick={onToggle} onKeyDown={onTriggerKey}>
        {label}
        {current ? <span className="sr-only"> (the page you are on is in this menu)</span> : null}
        <span className="menu-caret" aria-hidden="true">▾</span>
      </button>
      {open ? (
        <ul id={id} ref={list} className={align === "right" ? "menu-panel right" : "menu-panel"}
            onKeyDown={onListKey}>
          {children}
        </ul>
      ) : null}
    </li>
  );
}
